import {isEmptyArray} from 'formik'
import {capitalize, find, isEmpty} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {CSSTransition} from 'react-transition-group'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {useAuth} from '../../../../auth'
import {SettingsConfig} from '../../../core/_const'
import SettingsService from '../../../core/_requests'
import {ImmediateManifestModal} from '../../../modals/shipping/labels-slips/ImmediateManifestModal'
import {LabelsModal} from '../../../modals/shipping/labels-slips/LabelsModal'
import {PackingSlipsModal} from '../../../modals/shipping/labels-slips/PackingSlipsModal'

const LabelsSlipsPage: React.FC = () => {
  const intl = useIntl()
  const {currentAccount} = useAuth()
  const [settings, setSettings] = useState<any>([])
  const [companyLocation, setCompanyLocation] = useState<any>([])
  const [loadingFirst, setLoadingFirst] = useState<boolean>(true)
  const {newCancelToken} = useCancelToken()
  const [showModal, setShowModal] = useState<any>({
    labels: false,
    slips: false,
    manifest: false,
  })

  const getCompany = useCallback(async () => {
    try {
      const res = await SettingsService.getCompany({
        cancelToken: newCancelToken(),
      })
      if (res) {
        setCompanyLocation({
          address: res.street1,
          address_2: res.street2,
          country: res.country,
          city: res.city,
          state: res.state,
          zip: res.zip,
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
    }
  }, [newCancelToken])

  const getSettings = useCallback(async () => {
    try {
      setLoadingFirst(true)
      const settings = await SettingsService.settings({cancelToken: newCancelToken()})
      if (isEmpty(settings?.packing_slip?.store_location)) {
        await getCompany()
      }
      setSettings(settings)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingFirst(false)
    }
  }, [getCompany, newCancelToken])

  const buildSkeleton = useMemo(() => {
    return (
      <>
        <div className='mt-8'>
          <div className='d-flex justify-content-center'>
            <div className='w-100 w-sm-75 bg-light rounded p-8'>
              <div className='d-flex justify-content-between'>
                <div className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <span className='fs-1 col-1 placeholder rounded-2 bg-secondary' />
              </div>
              <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
              <div className='col-12 d-flex justify-content-between'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
              <div className='col-12 d-flex justify-content-between mt-6'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
            </div>
          </div>
        </div>

        <div className='mt-8'>
          <div className='d-flex justify-content-center'>
            <div className='w-100 w-sm-75 bg-light rounded p-8'>
              <div className='d-flex justify-content-between'>
                <div className='fs-1 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <span className='fs-1 col-1 placeholder rounded-2 bg-secondary' />
              </div>
              <div className='fs-2 col-12 placeholder placeholder-lg rounded-2 bg-secondary mt-6 mb-10' />
              <div className='col-12 d-flex justify-content-between'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
              <div className='col-12 d-flex justify-content-between mt-6'>
                <div className='fs-2 col-3 placeholder placeholder-lg rounded-2 bg-secondary' />
                <div className='fs-2 col-7 placeholder placeholder-lg rounded-2 bg-secondary' />
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }, [])

  useEffect(() => {
    getSettings()
  }, [getSettings])

  return (
    <>
      {showModal.labels && (
        <LabelsModal
          show={showModal.labels}
          handleClose={() => {
            getSettings()
            setShowModal((prev) => ({...prev, labels: false}))
          }}
          data={settings?.print}
        />
      )}
      {showModal.slips && (
        <PackingSlipsModal
          show={showModal.slips}
          handleClose={() => {
            getSettings()
            setShowModal((prev) => ({...prev, slips: false}))
          }}
          data={{
            ...settings?.packing_slip,
            company_location: companyLocation,
          }}
        />
      )}
      {showModal.manifest && (
        <ImmediateManifestModal
          show={showModal.manifest}
          handleClose={() => {
            getSettings()
            setShowModal((prev) => ({...prev, manifest: false}))
          }}
          data={{
            immediate_manifest: settings?.manifest?.immediate_manifest ?? false,
          }}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='mb-4'>
          {loadingFirst ? (
            <>{buildSkeleton}</>
          ) : (
            <div className='d-flex flex-column align-items-center justify-content-center'>
              <div className='d-flex justify-content-start w-100 settings-header'>
                <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
                  {intl.formatMessage({id: 'LABELS_AND_PACKING_SLIPS'})}
                </h1>
              </div>

              <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                <div className='d-flex justify-content-between fw-bolder'>
                  <span className='fs-3 fw-bolder text-gray-700'>
                    {intl.formatMessage({id: 'LABELS_AND_PACKING_SLIPS'})}
                  </span>
                  <span
                    className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder'
                    onClick={() => setShowModal((prev) => ({...prev, labels: true}))}
                  >
                    {intl.formatMessage({id: 'EDIT'})}
                  </span>
                </div>

                <p className='form-label text-gray-600'>
                  {intl.formatMessage({
                    id: 'LABEL_TYPE_CONTENT',
                  })}
                </p>

                <div className='mt-6'>
                  {Object.entries(SettingsConfig.LABELS_SLIPS.LABELS).map(([key, item], index) => {
                    switch (item.value) {
                      case SettingsConfig.LABELS_SLIPS.LABELS.INCLUDE_SLIPS.value:
                        return (
                          <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                            <div className='col-6 col-sm-4 fw-bold'>
                              {intl.formatMessage({id: item.label})}
                            </div>
                            <div className='text-wrap fw-bolder'>
                              {intl.formatMessage({
                                id: settings?.print?.[item.value] ? 'YES' : 'NO',
                              })}
                            </div>
                          </div>
                        )
                      case SettingsConfig.LABELS_SLIPS.LABELS.TYPE.value:
                      case SettingsConfig.LABELS_SLIPS.LABELS.SIZE.value:
                        return (
                          <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                            <div className='col-6 col-sm-4 fw-bold'>
                              {intl.formatMessage({id: item.label})}
                            </div>
                            <div className='text-wrap fw-bolder'>
                              {SettingsConfig?.[`OPTIONS_LABEL_${key}`].find(
                                (option) => option.value === settings?.print?.[item.value]
                              )?.label || ''}
                            </div>
                          </div>
                        )
                      case SettingsConfig.LABELS_SLIPS.LABELS.PRINT_ORDER.value:
                        return (
                          <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                            <div className='col-6 col-sm-4 fw-bold'>
                              {intl.formatMessage({id: 'PRINT_ORDER'})}
                            </div>
                            <div className='text-wrap fw-bolder'>
                              {intl.formatMessage({id: `SORT`})}
                              {intl.formatMessage({
                                id:
                                  SettingsConfig?.OPTIONS_PRINT_COLUMN.find(
                                    (option) => option.value === settings?.print?.print_column
                                  )?.label || 'BAE_ID',
                              })}
                              {intl.formatMessage({id: `IN`})}
                              {intl.formatMessage({
                                id:
                                  SettingsConfig?.[`OPTIONS_PRINT_ORDER`].find(
                                    (option) => option.value === settings?.print?.print_order
                                  )?.label || 'DESCENDING',
                              })}
                              {intl.formatMessage({id: `SORT_ORDER`})}
                            </div>
                          </div>
                        )
                      default:
                        return null
                    }
                  })}
                </div>
              </div>
              <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                <div>
                  <div className='d-flex flex-wrap justify-content-between'>
                    <span className='fs-3 fw-bolder text-gray-700'>
                      {intl.formatMessage({id: 'PACKING_SLIP_CONTENT'})}
                    </span>
                    <span
                      className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder ms-4'
                      onClick={() => setShowModal((prev) => ({...prev, slips: true}))}
                    >
                      {intl.formatMessage({id: 'EDIT'})}
                    </span>
                  </div>

                  <p className='form-label text-gray-600'>
                    {intl.formatMessage({
                      id: 'TAILOR_YOUR_PACKING_SLIPS_TO_MATCH_YOUR_NEED_AND_BRANDING',
                    })}
                  </p>

                  <div className='mt-6'>
                    {Object.values(SettingsConfig.LABELS_SLIPS.SLIPS).map((row, index) => {
                      const SLIPS_CONFIG = SettingsConfig.LABELS_SLIPS.SLIPS
                      const value = settings.packing_slip?.[row.value]

                      const renderRow = (label, content, append?) => (
                        <div className='d-flex text-gray-700 fs-5 mt-8' key={index}>
                          <div className='col-6 col-sm-4 fw-bold'>
                            {intl.formatMessage({id: label})}
                            {append}
                          </div>
                          <div className='text-wrap fw-bolder'>
                            {label !== SLIPS_CONFIG.STORE_LOGO.label
                              ? capitalize(content)
                              : content}
                          </div>
                        </div>
                      )

                      switch (row.value) {
                        case SLIPS_CONFIG.ORDER_NUMBER.value:
                          return renderRow(
                            row.label,
                            intl.formatMessage({
                              id: value
                                ? find(SettingsConfig.OPTIONS_ORDER, {value: value})?.label
                                : 'NONE',
                            }),
                            ' #'
                          )
                        case SLIPS_CONFIG.BARCODE.value:
                          return renderRow(
                            row.label,
                            intl.formatMessage({
                              id: value
                                ? find(SettingsConfig.OPTIONS_BARCODE, {value: value})?.label
                                : 'NONE',
                            })
                          )
                        case SLIPS_CONFIG.ORDER_DATE.value:
                        case SLIPS_CONFIG.SHIP_TO.value:
                        case SLIPS_CONFIG.SHIPPING_SERVICE.value:
                        case SLIPS_CONFIG.ITEM_DETAIL.value:
                          return renderRow(
                            row.label,
                            intl.formatMessage({id: value ? 'ON' : 'OFF'})
                          )
                        case SLIPS_CONFIG.TRACKING.value:
                          return renderRow(
                            row.label,
                            intl.formatMessage({id: value ? 'ON' : 'OFF'}),
                            ' #'
                          )
                        case SLIPS_CONFIG.STORE_LOGO.value:
                          return renderRow(
                            row.label,
                            !isEmpty(value) ? (
                              <div className={'col-8'}>
                                <div className='w-50 overflow-hidden'>
                                  <img
                                    src={value?.url_show}
                                    className='rounded-2 w-100'
                                    alt='img-file'
                                  />
                                  <div className='text-truncate mt-2'>{value?.name}</div>
                                </div>
                              </div>
                            ) : (
                              intl.formatMessage({id: 'NONE'})
                            )
                          )
                        case SLIPS_CONFIG.STORE_NAME.value:
                          const storeName =
                            value === false ? 'NONE' : !!value ? value : currentAccount?.name
                          return renderRow(
                            row.label,
                            storeName === 'NONE' ? intl.formatMessage({id: 'NONE'}) : storeName
                          )
                        case SLIPS_CONFIG.THIRD_PARTY_NAME.value:
                          return renderRow(
                            row.label,
                            intl.formatMessage({id: value ? 'YES' : 'NO'})
                          )
                        case SLIPS_CONFIG.STORE_LOCATION.value:
                          let storeLocation
                          const location = isEmptyArray(value)
                            ? companyLocation
                            : value === false
                            ? 'NONE'
                            : value

                          if (location === 'NONE') {
                            return renderRow(row.label, intl.formatMessage({id: 'NONE'}))
                          } else {
                            const {
                              address = '',
                              address_2 = '',
                              city = '',
                              state = '',
                              zip = '',
                            } = location
                            storeLocation = `${address ? address : ''}${
                              address_2 ? ` ${address_2},` : address ? ',' : ''
                            }${city ? ` ${city}, ` : ''}${state ? ` ${state},` : ''}${
                              zip ? ` ${zip}` : ''
                            }`

                            return renderRow(row.label, storeLocation)
                          }
                        default:
                          return renderRow(
                            row.label,
                            isEmpty(value) ? intl.formatMessage({id: 'NONE'}) : value
                          )
                      }
                    })}
                  </div>
                </div>
              </div>

              <div className='card settings-card w-100 border border-secondary rounded-3 py-6 px-8 mt-6'>
                <div>
                  <div className='d-flex flex-wrap justify-content-between'>
                    <span className='fs-3 fw-bolder text-gray-700'>
                      {intl.formatMessage({id: 'IMMEDIATE_MANIFEST'})}
                    </span>
                    <span
                      className='fs-5 btn btn-advanced-tertiary px-2 py-1 cursor-pointer fw-bolder ms-4'
                      onClick={() => setShowModal((prev) => ({...prev, manifest: true}))}
                    >
                      {intl.formatMessage({id: 'EDIT'})}
                    </span>
                  </div>

                  <p className='form-label text-gray-600'>
                    {intl.formatMessage({
                      id: 'IMMEDIATE_MANIFEST_DESCRIPTION',
                    })}
                  </p>

                  <div className='mt-6'>
                    <div className='d-flex text-gray-700 fs-5 mt-8'>
                      <div className='col-6 col-sm-4 fw-bold'>
                        {intl.formatMessage({id: 'MANIFEST_SETTING'})}
                      </div>
                      <div className='text-wrap fw-bolder'>
                        {intl.formatMessage({
                          id: settings?.manifest?.immediate_manifest
                            ? 'MANIFEST_IMMEDIATELY'
                            : 'DO_NOT_MANIFEST_IMMEDIATELY',
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  )
}

export {LabelsSlipsPage}
